/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { AutocompleteItem } from '@gh/common-shared-data';
import {
  bySelectionModel,
  emptySelectionModel,
  emptySlicedDataSource,
  SelectionModel,
  SlicedDataSource,
} from '@gh/core-data';
import { AutocompleteLoadMoreEvent, NotificationService, WaitingService } from '@gh/core-ui';
import { BulkUpdateBusinessService } from '../../resources/bulk-update-business.service';
import {
  BulkUpdateComponentAdd,
  ComponentBulkUpdateListItem,
  getItemBulkUpdateId,
  ItemBulkUpdateListItem,
} from '../../resources/bulk-update.model';
import { BulkUpdateAbstractComponent } from '../bulk-update-abstract.component';
import { ComponentGridDefinition } from '../component-grid-definition.service';
import { ItemGridDefinition } from './item-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-bulk-update-component-add',
  templateUrl: 'bulk-update-component-add.component.html',
  styleUrls: ['bulk-update-component-add.component.scss'],
  providers: [ItemGridDefinition, ComponentGridDefinition],
})
export class BulkUpdateComponentAddComponent extends BulkUpdateAbstractComponent {
  sourceComponent?: AutocompleteItem;
  sourceItems: ItemBulkUpdateListItem[] = [];
  targetDataSource: SlicedDataSource<ComponentBulkUpdateListItem>;
  sourceSelectionModel = emptySelectionModel();
  targetSelectionModel = emptySelectionModel();
  dirty: boolean = false;

  @ViewChild('sourceComponentModel', { static: false }) sourceComponentModel: NgModel;

  constructor(public itemGridDefinition: ItemGridDefinition,
              public componentGridDefinition: ComponentGridDefinition,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private businessService: BulkUpdateBusinessService) {
    super();
  }

  loadSourceComponents($event: AutocompleteLoadMoreEvent): void {
    $event.load(this.businessService.loadComponents($event.query));
  }

  onChangeSourceComponent(component?: AutocompleteItem): void {
    this.sourceComponent = component;
    this.sourceSelectionModel = emptySelectionModel();
    this.resetDirty(true);

    if (component) {
      this.businessService.loadItemsByComponent(component.id).pipe(
        this.waitingService.operator<ItemBulkUpdateListItem[]>())
        .subscribe((res) => this.sourceItems = res);
      this.load();
    } else {
      this.sourceItems = [];
      this.targetDataSource = emptySlicedDataSource();
      this.resetDirty(false);
    }
  }

  onSave(): void {
    this.businessService.addComponentSave(this.getRequestBody()).pipe(
      this.waitingService.operator<any>())
      .subscribe(
        (res) => this.afterSave(),
        this.notificationService.processAll,
      );
  }

  onSourceSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.sourceSelectionModel = model;
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.targetSelectionModel = model;
  }

  load(): void {
    this.targetDataSource = this.businessService.loadComponentGridDataSource(
      this.sourceComponent && this.sourceComponent.id);
  }

  protected afterSave(): void {
    this.notificationService.success('msg_bulk_update_initiated_successfully');
    this.sourceComponent = void 0;
    this.sourceItems = [];
    this.sourceComponentModel.control.markAsUntouched();
    this.targetSelectionModel = emptySelectionModel();
    this.sourceSelectionModel = emptySelectionModel();
    this.resetDirty(false);
  }

  protected getRequestBody(): BulkUpdateComponentAdd {
    return {
      targetComponents: this.targetSelectionModel,
      items: this.sourceItems
        .filter(bySelectionModel(this.sourceSelectionModel, getItemBulkUpdateId))
        .map(getItemBulkUpdateId),
    };
  }
}
